import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosClient } from 'hooks/utils';
import { saveSchool, SaveSchoolInput } from './saveSchool';
import * as MN from '@mantine/notifications';
import type { ApiTypes } from 'types';
import type { CamelCasedPropertiesDeep } from 'type-fest';

// Organization fields returned by the API are missing from the generated schema.
export type SchoolListItem = CamelCasedPropertiesDeep<
  ApiTypes['SchoolViewExtra']
> & {
  organizationType?: 'high_school' | 'college';
  ipedsId?: string | null;
  country?: 'US' | 'CA';
};

export type SchoolQueryFilterKey = keyof (ApiTypes['SchoolGlobalSearchView'] &
  ApiTypes['SchoolView']);

type SchoolsQueryParams = {
  page: number;
  limit: number;
  order_by?: 'asc' | 'desc';
  sort_by?: SchoolQueryFilterKey;
  filter_by?: `${SchoolQueryFilterKey}:${
    | 'eq'
    | 'gt'
    | 'lt'
    | 'gte'
    | 'lte'
    | 'ne'}:${string | number}`;
  search?: string;
};

export const useSchoolsFiltered = (params: SchoolsQueryParams) => {
  return useQuery({
    queryKey: ['schools', params],
    keepPreviousData: true,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, headers } = await axiosClient.get<SchoolListItem[]>(
        'v2/schools',
        {
          params: { ...params, paginate: true },
        },
      );

      return {
        schools: data,
        schoolsTotal: parseInt(headers['xPaginationItems']),
        usersTotal: parseInt(headers['xUserCount']),
      };
    },
  });
};

export const useSaveSchool = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveSchool,
    // A lost create response must never cause an automatic second write.
    retry: false,
    onSuccess: async (
      _,
      { mode, organizationType, school }: SaveSchoolInput,
    ) => {
      await Promise.all([
        queryClient.invalidateQueries(['schools']),
        queryClient.invalidateQueries(['school', school.schoolName]),
        queryClient.invalidateQueries(['schoolDetails', school.schoolName]),
        queryClient.invalidateQueries(['schoolInfo', school.schoolName]),
      ]);
      MN.showNotification({
        message: `${organizationType === 'college' ? 'College' : 'School'} ${
          mode === 'create' ? 'created' : 'updated'
        } successfully`,
        color: 'green',
      });
    },
  });
};

// TODO: this should go somewhere else
export const useDistricts = () => {
  return useQuery({
    queryKey: ['districts'],
    queryFn: async () => {
      const { data } = await axiosClient.get<
        {
          districtName: string;
          districtCode: string;
        }[]
      >('v2/map/districts');
      return data;
    },
  });
};
